import { isEqual, last } from 'lodash-es'
import { defineStore } from 'pinia'

import { useAnnouncementsStore } from 'src/stores/announcements.js'
import { useBookmarkFoldersStore } from 'src/stores/bookmark_folders.js'
import { useChatsStore } from 'src/stores/chats.js'
import { useDraftsStore } from 'src/stores/drafts.js'
import { useEmojiStore } from 'src/stores/emoji.js'
import { useFollowRequestsStore } from 'src/stores/follow_requests.js'
import { useFollowedTagsStore } from 'src/stores/followed_tags.js'
import { useInstanceStore } from 'src/stores/instance.js'
import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'
import { useInterfaceStore } from 'src/stores/interface.js'
import { useListsStore } from 'src/stores/lists.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useNotificationsStore } from 'src/stores/notifications.js'
import { useOAuthStore } from 'src/stores/oauth.js'
import { useOAuthTokensStore } from 'src/stores/oauth_tokens.js'
import { usePollsStore } from 'src/stores/polls.js'
import { useProfileConfigStore } from 'src/stores/profile_config.js'
import { useReportsStore } from 'src/stores/reports.js'
import { useScrobblesStore } from 'src/stores/scrobbles.js'
import { useServerFiltersStore } from 'src/stores/server_filters.js'
import { useShoutStore } from 'src/stores/shout.js'
import { useStatusesStore } from 'src/stores/statuses.js'
import { useStreamingStore } from 'src/stores/streaming.js'
import { useSyncConfigStore } from 'src/stores/sync_config.js'
import { useTimelinesStore } from 'src/stores/timelines.js'
import { useUserHighlightStore } from 'src/stores/user_highlight.js'
import { useWhoToFollowStore } from 'src/stores/who_to_follow.js'

import { revokeToken } from 'src/api/oauth.js'
import {
  fetchFollowers,
  fetchFriends,
  fetchUser,
  fetchUserByName,
  verifyCredentials,
} from 'src/api/public.js'
import {
  blockUser,
  editUserNote,
  endorseUser,
  fetchBlocks,
  fetchDomainMutes,
  fetchEndorsements,
  fetchMutes,
  fetchUserInLists,
  fetchUserRelationship,
  followUser,
  muteDomain,
  muteUser,
  removeUserFromFollowers,
  unblockUser,
  unendorseUser,
  unfollowUser,
  unmuteDomain,
  unmuteUser,
} from 'src/api/user.js'
import { WSConnectionStatus } from 'src/api/websocket.js'
import { parseLinkHeaderPagination } from 'src/services/entity_normalizer/entity_normalizer.service.js'
import { applyLayoutVariables } from 'src/services/layout_vars/layout_vars.js'
import { promiseInterval } from 'src/services/promise_interval/promise_interval.js'

// the next page of each list of follows (see fetchFollowPage): kept by the
// list itself, so clearing the list starts it over
const followCursors = new WeakMap()

// Stores that poll for the logged-in account: started at login, stopped at
// logout (and started again when the logout fails).
const accountPollers = () => [
  useAnnouncementsStore(),
  useListsStore(),
  useBookmarkFoldersStore(),
  useFollowRequestsStore(),
  useChatsStore(),
]

// Stores that hold the logged-in account's own data: emptied at logout, so
// the next account on the same page starts clean (login does not reload).
// Not the admin settings: they are the instance's, and the admin
// dashboard loads them only once.
const accountStores = () => [
  ...accountPollers(),
  useFollowedTagsStore(),
  useOAuthTokensStore(),
  usePollsStore(),
  useDraftsStore(),
  useReportsStore(),
  useScrobblesStore(),
  useWhoToFollowStore(),
]

// all of them: each poll fetches only what the server has, which it may
// tell only after the login (nodeinfo can answer after the account)
export const startAccountPollers = () =>
  accountPollers().forEach((store) => store.startFetching())

const stopAccountPollers = () =>
  accountPollers().forEach((store) => store.stopFetching())

// a relationship with something in it besides the user's id
const knownRelationship = (relationship) =>
  !!relationship && Object.keys(relationship).some((key) => key !== 'id')

const RELATIONSHIP_RETRY_MS = 1000
let pendingRelationships = null
let pendingRelationshipsDone = null
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export const useUsersStore = defineStore('users', {
  state: () => ({
    loggingIn: false,
    currentUser: null,
    users: new Map(),
    usersByName: new Map(),
    usersByURL: new Map(),
    relationships: new Map(),
    relationshipsLists: {
      friends: new WeakMap(),
      followers: new WeakMap(),
    },
    timestamps: new WeakMap(),
    fetchesIds: new Map(),
    fetchesNames: new Map(),
    followPollers: new Map(),
    followPollersAttempts: new Map(),
  }),
  getters: {
    loggedIn: (state) => !!state.currentUser,
    findUser: (state) => (query) => {
      return state.users.get(query)
    },
    findUserByName: (state) => (query) => {
      return state.usersByName.get(query.toLowerCase())
    },
    findUserByUrl: (state) => (query) => {
      return state.usersByURL.get(query.toLowerCase())
    },
    relationship: (state) => (id) => {
      const rel = id && state.relationships.get(id)
      return rel || { id, loading: true }
    },
  },
  actions: {
    // Main updates
    addNewUsers(response) {
      const { data, timestamp } = response
      const users = Array.isArray(data) ? data : [data]

      return users.map((user) => {
        const existing = this.users.get(user.id) ?? {}
        const oldTimestamp = this.timestamps.get(existing)
        //
        // Relationship might have different timestamp and
        // might need updating separate from user
        const oldRelationship = this.relationships.get(user.id)
        const oldRelationshipTimestamp = this.timestamps.get(oldRelationship)
        let relationship

        // Only need an update if new user data has relationship; posts
        // carry an empty one ({}), which says nothing
        if (knownRelationship(user.relationship)) {
          // Only update if there is no old data or if it's outdated
          if (
            oldRelationship === undefined ||
            timestamp > oldRelationshipTimestamp
          ) {
            relationship = this.updateUserRelationships({
              timestamp,
              data: { id: user.id, ...user.relationship },
            })[0]
          }
        } else {
          relationship = oldRelationship
        }

        // implicit: if oldTimestamp is undefined this will still be false
        if (oldTimestamp > timestamp) return existing // not overwriting old data with new

        const { relationship: unused1, ...newUser } = user

        let reactive = this.users.get(user.id)
        // Initializing reactivity & avoiding excessive Map mutation
        if (!reactive) {
          this.users.set(user.id, existing)

          reactive = this.users.get(user.id)
          this.usersByName.set(user.screen_name.toLowerCase(), reactive)
          this.usersByURL.set(user.url.toLowerCase(), reactive)
        }

        // Relying on object reactivity to avoid mutating the Map
        reactive.relationship = relationship ?? reactive.relationship

        // polls repeat the same data: a fresh but equal object would still
        // re-render everything that shows this user
        // (_original, the raw reply, is only read on demand: not compared)
        Object.entries(newUser).forEach(([k, v]) => {
          const plain = typeof v !== 'object' || v === null
          if (plain || k === '_original' || !isEqual(reactive[k], v)) {
            reactive[k] = v
          }
        })

        // Updating the timestamp
        this.timestamps.set(reactive, timestamp)

        if (user.id === this.currentUser?.id) {
          this.currentUser = reactive
          useProfileConfigStore().update(reactive)
        }

        // Initialize some stuff
        const { friends, followers } = this.relationshipsLists
        if (!friends.has(reactive)) friends.set(reactive, new Set())
        if (!followers.has(reactive)) followers.set(reactive, new Set())

        return reactive
      })
    },
    updateUserRelationships({ timestamp, optimism, data }) {
      const relationships = Array.isArray(data) ? data : [data]

      return relationships.map((relationship) => {
        const { id } = relationship
        const existing = this.relationships.get(id) ?? {}
        const oldTimestamp = this.timestamps.get(existing)

        // implicit: if oldTimestamp is undefined this will still be false
        if (!optimism && oldTimestamp > timestamp) return existing

        // Initializing reactivity
        if (!this.relationships.has(id)) this.relationships.set(id, existing)
        const reactive = this.relationships.get(id)

        // Relying on reactivity
        Object.entries(relationship).forEach(([k, v]) => {
          reactive[k] = v
        })

        if (timestamp !== undefined) {
          this.timestamps.set(reactive, timestamp)
        }

        // Updating user property if there is such a user
        if (this.users.has(id)) {
          this.users.get(id).relationship = reactive
        }

        // Update block/mute lists
        if (this.currentUser && id !== this.currentUser.id) {
          ;[
            ['muting', this.currentUser.muteIds],
            ['blocking', this.currentUser.blockIds],
            [
              'following',
              this.relationshipsLists.friends.get(this.currentUser),
            ],
            [
              'followed_by',
              this.relationshipsLists.followers.get(this.currentUser),
            ],
          ].forEach(([relationshipName, list]) => {
            if (relationship[relationshipName]) {
              list?.add(id)
            } else {
              list?.delete(id)
            }
          })
        }

        return reactive
      })
    },

    // Misc updates
    updateUserAdminData(id, data) {
      const user = this.users.get(id)
      if (!user) {
        console.warn(
          `User id ${id} somehow not found during admin data update!`,
        )
        return
      }
      user.adminData = data
      user.deactivated = !data.is_active
      user.tags = new Set(data.tags)
    },
    updateRight(id, right, value) {
      const user = this.users.get(id)
      const newRights = user.rights ?? {}
      newRights[right] = value
      user.rights = newRights
    },

    // Because frontend doesn't have a reason to keep these stuff in memory
    // outside of viewing someones user profile.
    clearFollowLists(userId) {
      const user = this.users.get(userId)
      if (user) {
        this.relationshipsLists.friends.set(user, new Set())
        this.relationshipsLists.followers.set(user, new Set())
      }
    },

    // Fetches
    async fetchUserIfMissing({ id, name }) {
      let findFunc
      let fetchFunc
      let map
      let identifier

      if (id) {
        findFunc = this.findUser
        fetchFunc = this.fetchUser
        map = this.fetchesIds
        identifier = id
      } else if (name) {
        findFunc = this.findUserByName
        fetchFunc = this.fetchUserByName
        map = this.fetchesNames
        identifier = name
      } else {
        throw new TypeError('No identifier provided')
      }

      // Search in cache
      const user = findFunc(identifier)

      if (user) return user
      // not found => fetch
      let promise

      // Did we already search for this user?
      if (map.has(identifier)) {
        // if so, reuse the promise
        promise = map.get(identifier)
      } else {
        // if not, make a new one
        promise = fetchFunc(identifier)
      }

      map.set(identifier, promise)

      try {
        const result = await promise
        if (result) {
          const { id, screen_name } = result

          // Save promise for future use
          this.fetchesIds.set(id, promise)
          this.fetchesNames.set(screen_name, promise)
          return this.users.get(id)
        } else {
          return null
        }
      } catch (e) {
        console.error(`Failed fetching user ${identifier}`, e)
        map.delete(identifier)
        throw e
      }
    },
    async fetchUser(id) {
      try {
        const result = await fetchUser({
          id,
        })

        this.addNewUsers(result)

        return this.users.get(result.data.id)
      } catch (error) {
        if (error.name === 'StatusCodeError' && error.statusCode === 404) {
          console.warn(`User ${id} not found`)
          return null
        } else {
          throw error
        }
      }
    },
    async fetchUserByName(name) {
      try {
        const result = await fetchUserByName({
          name,
        })

        this.addNewUsers(result)

        return this.users.get(result.data.id)
      } catch (error) {
        if (error.name === 'StatusCodeError' && error.statusCode === 404) {
          console.warn(`User ${name} not found`)
          return null
        } else {
          throw error
        }
      }
    },
    // Asked when a user card opens (one id) or for a list (many ids). A
    // failed request is asked again once; ids the server still has no
    // answer for stop waiting and show the follow button as before (an
    // empty relationship, timestamp 0 so any later answer wins).
    fetchUserRelationship(ids) {
      const wanted = [ids].flat()
      const ask = () =>
        fetchUserRelationship({
          id: wanted.length === 1 ? wanted[0] : wanted,
        }).then((result) => {
          if (![result.data].flat().length) throw new Error('no answer')
          return result
        })
      return ask()
        .catch(() => wait(RELATIONSHIP_RETRY_MS).then(ask))
        .then((result) => this.updateUserRelationships(result))
        .catch((error) => {
          console.warn(`Relationships with ${wanted} not available`, error)
        })
        .finally(() => {
          wanted
            .filter((id) => !this.relationships.has(id))
            .forEach((id) =>
              this.updateUserRelationships({ timestamp: 0, data: { id } }),
            )
        })
    },
    // for lists of users: ids asked for together are fetched in one request
    requestRelationship(id) {
      if (!pendingRelationships) {
        pendingRelationships = new Set()
        pendingRelationshipsDone = wait(0).then(() => {
          const ids = [...pendingRelationships]
          pendingRelationships = null
          return this.fetchUserRelationship(ids)
        })
      }
      pendingRelationships.add(id)
      return pendingRelationshipsDone
    },
    // a page of the accounts a user follows, or of their followers: where
    // the server's Link header points (Mastodon and GoToSocial page by the
    // follow, not the account), else after the last account; none after
    // the last page
    async fetchFollowPage(id, kind, fetchPage) {
      const list = this.relationshipsLists[kind].get(this.users.get(id))
      const cursor = followCursors.get(list)
      if (cursor === null) return []
      const result = await fetchPage({ id, maxId: cursor })
      const users = this.addNewUsers(result)
      users.forEach(({ id }) => list.add(id))
      const link = result.response?.headers.get('Link')
      followCursors.set(
        list,
        link
          ? (parseLinkHeaderPagination(link, { flakeId: true })?.maxId ?? null)
          : (users.at(-1)?.id ?? null),
      )
      return result.data
    },
    fetchFriends(id) {
      return this.fetchFollowPage(id, 'friends', fetchFriends)
    },
    fetchFollowers(id) {
      return this.fetchFollowPage(id, 'followers', fetchFollowers)
    },
    fetchUserInLists(id) {
      if (this.currentUser) {
        return fetchUserInLists({
          id,
        }).then(({ data: inLists }) => {
          this.users.get(id).inLists = inLists
        })
      }
    },
    fetchMutes(args) {
      const { reset } = args || {}

      const maxId = this.currentUser.muteIdsMaxId
      return fetchMutes({
        maxId,
      }).then((result) => {
        const { data: mutes } = result
        if (reset) {
          this.currentUser.muteIds = new Set(mutes.map(({ id }) => id))
        } else {
          mutes.forEach(({ id }) => this.currentUser.muteIds.add(id))
        }
        if (mutes.length) {
          this.currentUser.muteIdsMaxId = last(mutes).id
        }
        this.addNewUsers(result)
        return mutes
      })
    },
    fetchBlocks(args) {
      const { reset } = args || {}

      const maxId = this.currentUser.blockIdsMaxId
      return fetchBlocks({
        maxId,
      }).then((result) => {
        const { data: blocks } = result
        if (reset) {
          this.currentUser.blockIds = new Set(blocks.map(({ id }) => id))
        } else {
          blocks.forEach(({ id }) => this.currentUser.blockIds.add(id))
        }
        if (blocks.length) {
          this.currentUser.blockIdsMaxId = last(blocks).id
        }
        this.addNewUsers(result)
        return blocks
      })
    },
    fetchDomainMutes() {
      return fetchDomainMutes({}).then(({ data: domainMutes }) => {
        this.currentUser.domainMutes = new Set(domainMutes)
        return domainMutes
      })
    },

    // Actions
    /// Follow
    async followUser(id) {
      // Don't spam follow requests if we are already polling
      if (this.followPollers.has(id)) return

      const followFunc = () =>
        followUser({
          id,
        }).then((result) => this.updateUserRelationships(result))

      const checker = async (func = () => this.fetchUserRelationship(id)) => {
        await func() // Either fetch relationships or follow request
        const relationship = this.relationships.get(id)

        return (
          relationship.following ||
          (relationship.locked && relationship.requested)
        )
      }

      const immediate = await checker(followFunc)
      if (immediate) return // If follow goes through immediately don'd to looping

      const loop = async () => {
        const attempts = this.followPollersAttempts.get(id)
        const result = await checker()

        if (result || attempts === 1) {
          this.followPollersAttempts.delete(id)
          this.followPollers.get(id).stop()
          this.followPollers.delete(id)
        } else {
          this.followPollersAttempts.set(id, attempts - 1)
        }
      }

      this.followPollersAttempts.set(id, 3)
      this.followPollers.set(id, promiseInterval(loop, 500))
    },
    async unfollowUser(id) {
      const result = await unfollowUser({
        id,
      })

      return this.updateUserRelationships(result)
    },

    /// Subscribe
    subscribeUser(id) {
      return followUser({
        id,
        notify: true,
      }).then((result) => this.updateUserRelationships(result))
    },
    unsubscribeUser(id) {
      return followUser({
        id,
        notify: false,
      }).then((result) => this.updateUserRelationships(result))
    },

    /// User Note
    editUserNote(id, comment) {
      return editUserNote({
        id,
        comment,
      }).then((result) => this.updateUserRelationships(result))
    },

    /// Hide reblogs
    hideReblogs(id) {
      return followUser({
        id,
        reblogs: false,
      }).then((result) => this.updateUserRelationships(result))
    },
    showReblogs(id) {
      return followUser({
        id,
        reblogs: true,
      }).then((result) => this.updateUserRelationships(result))
    },

    /// Remove follower
    removeUserFromFollowers(id) {
      return removeUserFromFollowers({
        id,
      }).then((result) => this.updateUserRelationships(result))
    },

    /// Mute
    // shown at once; when the server refuses, it goes back
    setMuting(id, muting, request) {
      const before = !!this.relationships.get(id)?.muting
      const predict = (value) =>
        this.updateUserRelationships({
          optimism: true,
          data: [{ ...(this.relationships.get(id) || { id }), muting: value }],
        })
      predict(muting)
      return request().then(
        (result) => this.updateUserRelationships(result),
        (error) => {
          predict(before)
          throw error
        },
      )
    },
    muteUser(id, expiresIn = 0) {
      return this.setMuting(id, true, () => muteUser({ id, expiresIn }))
    },
    endorseUser(id) {
      return endorseUser({ id }).then((result) =>
        this.updateUserRelationships(result),
      )
    },
    unendorseUser(id) {
      return unendorseUser({ id }).then((result) =>
        this.updateUserRelationships(result),
      )
    },
    // the accounts a profile features, added to the known users
    async fetchEndorsements(id) {
      const result = await fetchEndorsements({
        id,
      })
      this.addNewUsers(result)
      return result.data.map((user) => this.findUser(user.id))
    },
    muteUsers(data = []) {
      return Promise.all(data.map((d) => this.muteUser(d)))
    },
    unmuteUser(id) {
      return this.setMuting(id, false, () => unmuteUser({ id }))
    },
    unmuteUsers(ids = []) {
      return Promise.all(ids.map((d) => this.unmuteUser(d)))
    },

    /// Block
    blockUser(id, expiresIn = 0) {
      const predictedRelationship = this.relationships.get(id) || { id }
      this.updateUserRelationships({
        optimism: true,
        data: [predictedRelationship],
      })

      return blockUser({
        id,
        expiresIn,
      }).then((result) => {
        this.updateUserRelationships(result)

        const ids = useStatusesStore().wipeUserStatuses(id)
        useTimelinesStore().wipeStatuses(ids)
        useNotificationsStore().wipeStatuses(ids)
      })
    },
    blockUsers(data = []) {
      return Promise.all(data.map((d) => this.blockUser(d)))
    },
    unblockUser(id) {
      return unblockUser({ id }).then((result) => {
        this.updateUserRelationships(result)
      })
    },
    unblockUsers(data = []) {
      return Promise.all(data.map((d) => this.unblockUser(d)))
    },

    /// Domain Mute
    muteDomain(domain) {
      return muteDomain({
        domain,
      }).then(() => this.currentUser.domainMutes.add(domain))
    },
    unmuteDomain(domain) {
      return unmuteDomain({
        domain,
      }).then(() => this.currentUser.domainMutes.delete(domain))
    },
    unmuteDomains(domain = []) {
      return Promise.all(domain.map((domain) => this.unmuteDomain(domain)))
    },

    // Login/Logout
    async loginUser(accessToken) {
      this.loggingIn = true

      try {
        const { data: user, ...rest } = await verifyCredentials({})

        user.blockIds = new Set()
        user.muteIds = new Set()
        user.domainMutes = new Set()

        useTimelinesStore().deactivateAll()
        useStatusesStore().resetStatuses()

        this.users = new Map()
        this.usersByName = new Map()
        this.usersByURL = new Map()
        this.relationships = new Map()
        this.currentUser = user
        this.addNewUsers({ data: user, ...rest })

        useInterfaceStore().onLogin()
        // the synced settings may bring another theme mode or layout
        useSyncConfigStore()
          .initSyncConfig(user)
          .then(() => {
            useInterfaceStore().applyTheme()
            applyLayoutVariables(useMergedConfigStore().mergedConfig)
          })
        useProfileConfigStore().onLogin(user)

        useUserHighlightStore().initUserHighlight(user)

        useEmojiStore().fetchEmoji()

        if (user.token && useInstanceCapabilitiesStore().shoutAvailable) {
          // Shoutbox
          useShoutStore().initializeShout()
        }

        // DMs and Home
        useTimelinesStore().activatePersistents()
        useNotificationsStore().activate()

        startAccountPollers()

        if (useMergedConfigStore().mergedConfig.useStreamingApi) {
          useStreamingStore().initSocket(true)
        }

        this.fetchMutes()
        // the account's own drafts on this browser (hosted: any server)
        useDraftsStore().loadDrafts(
          user.fqn ||
            `${user.screen_name}@${new URL(useInstanceStore().server || '', window.location.href).host}`,
        )
        useServerFiltersStore()
          .load()
          .catch(() => {
            // filters are optional: a server without them posts as usual
          })
      } catch (error) {
        console.error(error)

        // Authentication failed
        this.loggingIn = false

        // remove authentication token on client/authentication errors
        if ([400, 401, 403, 422].includes(error.statusCode)) {
          useOAuthStore().clearToken()
        }

        if (error.statusCode === 401) {
          throw new Error('Wrong username or password', error)
        } else {
          throw new Error('An error occurred, please try again', error)
        }
      } finally {
        this.loggingIn = false
      }
    },
    // the saved login, at boot: when it fails (revoked, the server down)
    // the app starts logged out; true when it worked
    restoreLogin(accessToken) {
      return this.loginUser(accessToken).then(
        () => true,
        (error) => {
          console.warn('The saved login did not work', error)
          return false
        },
      )
    },
    logout() {
      const oauth = useOAuthStore()

      // Pause fetching
      useNotificationsStore().pause()
      useTimelinesStore().pauseAll()

      stopAccountPollers()

      // push is turned off while the login still works; if the logout
      // then fails, it comes back on
      let pushOff = false

      // NOTE: No need to verify the app still exists, because if it doesn't,
      // the token will be invalid too
      return oauth
        .ensureApp()
        .then(async (app) => {
          // while the token still works: the server drops the subscription
          // (only where the instance has push at all)
          if (useInstanceStore().vapidPublicKey) {
            await useInterfaceStore().unregisterPushNotifications()
            pushOff = true
          }
          return revokeToken({
            app,
            instance: useInstanceStore().server,
            token: oauth.userToken,
          })
        })
        .then(() => {
          oauth.clearToken()
          useShoutStore().disconnectSocket()

          this.currentUser = null

          useNotificationsStore().deactivate()

          // Full reset on logout success
          useTimelinesStore().deactivateAll()
          useStatusesStore().resetStatuses()
          useChatsStore().resetChats()
          useServerFiltersStore().$reset()
          // chats: resetChats above (their state shares one Map)
          accountStores()
            .filter((store) => store !== useChatsStore())
            .forEach((store) => store.$reset())

          this.users = new Map()
          this.usersByName = new Map()
          this.usersByURL = new Map()
          this.relationships = new Map()

          // Socket is most likely already closed by server
          if (
            useMergedConfigStore().mergedConfig.useStreamingApi &&
            useStreamingStore().state !== WSConnectionStatus.CLOSED
          ) {
            useStreamingStore().stopSocket()
          }

          useInterfaceStore().onLogout()
          useProfileConfigStore().onLogout()
        })
        .catch((e) => {
          useInterfaceStore().pushGlobalNotice({
            messageKey: 'login.logout_failure',
            messageArgs: {
              error: e,
            },
            level: 'error',
          })
          console.error('Logout error!', e)

          startAccountPollers()
          if (pushOff) useInterfaceStore().registerPushNotifications()
        })
        .finally(() => {
          useNotificationsStore().resume()
          useTimelinesStore().resumeAll()
        })
    },
  },
})
